import React from 'react';
import { connect } from 'react-redux';
import { withNavigation } from 'src/ui/hoc';

import { Card, CardContent, Columns, Column, MuiTitle } from 'src/ui/mui-ui-kit';
import { Form, Field } from 'react-final-form';
import propTypes from 'prop-types';
import dayjs from 'dayjs-ext';
import { get, find, differenceBy } from 'lodash';

import {
  GuidelinesCard,
  setBreadcrumbs,
  UiToastAdapter,
  InnerLoader,
  reduceServerErrors,
  // NextButton,
  maskPhone,
  ConfirmChangesModal,
  SaveChangesToast,
  UiFieldset,
  UiSelectAdapter,
  UiInputAdapter,
  UiDatePickerAdapter,
  UiQuillAdapter,
  UiToggleAdapter,
} from 'src/ui';
import {
  getAccount,
  getFormData,
  fetchFormData,
  updateAccount,
  updateAccountFI,
  addAccountWebsite,
  deleteAccountWebsite,
  deleteAccountDBA,
  addAccountDBA,
  addAccountLocation,
  updateAccountLocation,
} from 'src/account';
import { getUser } from 'src/user';
import {
  DEFAULT_DATE_FORMAT,
  FACEBOOK_BASE_URL,
  INSTAGRAM_BASE_URL,
  TWITTER_BASE_URL,
  LINKEDIN_BASE_URL,
  TAX_ID_MASK,
  PHONE_MASK,
} from 'src/ui/const';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cleanseInput, cleanseInt } from 'src/ui/util';
import {
  composeValidators,
  email,
  url,
  required,
  number,
} from 'src/ui/validations';
import arrayMutators from 'final-form-arrays';
import { usStates } from 'src/core';
import { ACCOUNT_ENTITY_TYPES } from '../const';
import { AddressForm } from '../components/AddressForm';
import { titleToCamelCase } from '../utils';

const guidelines = [
  'All company information provided must be valid (expired information will be declined)',
];

const currentCustomerOptions = [
  { label: 'New Customer', value: false },
  { label: 'Current Customer', value: true },
];

const nextPage = '/locations';

const LOCATION_TYPES = [
  { id: 1, name: 'Legal Address' },
  { id: 3, name: 'Physical Address' },
];

const processLocation = location => ({
  ...location,
  ...(location.state
    ? { state: find(usStates, { value: location.state }) }
    : {}),
});

const BusinessPage = ({
  isExpanded,
  formData,
  user,
  account,
  history,
  setBreadcrumbs,
  fetchFormData,
  updateAccount,
  updateAccountFI,
  deleteAccountWebsite,
  addAccountWebsite,
  deleteAccountDBA,
  addAccountDBA,
  addAccountLocation,
  updateAccountLocation,
}) => {
  const [loading, setLoading] = React.useState(true);
  const [initialValues, setInitialValues] = React.useState(null);
  const [confirmChangesModal, setConfirmChangesModal] = React.useState(false);
  const [goNext, setGoNext] = React.useState(false);
  // eslint-disable-next-line no-unused-vars
  const [forInsurance, setForInsurance] = React.useState(
    user.financialInstitutionForInsurance
  );
  const toastContainerClassName = classNames('bottom-toast-container', {
    'is-condensed': !isExpanded,
  });

  const fiId = React.useMemo(() => {
    return user.financialInstitutionId || account.financialInstitutions[0].id;
  }, [user, account.financialInstitutions]);

  const showModal = (goNext = false) => {
    setConfirmChangesModal(true);
    setGoNext(goNext);
  };

  const hideModal = () => {
    setConfirmChangesModal(false);
  };

  const getLocation = locationType => {
    return processLocation(
      account.locations.find(({ typeId }) => typeId === locationType.id) || {}
    );
  };

  const handleSubmit = async values => {
    /* Update account */
    await updateAccount({
      accountId: user.accountId,
      ...values,
      naics: values.naics || '',
      insuranceGroupNames: values.groupNames
        ? values.groupNames.map(({ label, value }) => ({
            id: Number.isInteger(value) ? value : null,
            insurance_group_name: label,
          }))
        : [],
      businessSince: values.businessSince
        ? dayjs(values.businessSince).format(DEFAULT_DATE_FORMAT)
        : null,
      industries: (values.industries || [])
        .filter(Boolean)
        .map(industry => industry.id),
      activities: (values.activities || [])
        .filter(Boolean)
        .map(activity => activity.id),
      entityType: values.entityType ? values.entityType.value : null,
      incorporationState: values.incorporationState
        ? values.incorporationState.value
        : null,
      entityStartDate: values.entityStartDate
        ? dayjs(values.entityStartDate).format(DEFAULT_DATE_FORMAT)
        : null,
      varSheetIssuedDate: values.varSheetIssuedDate
        ? dayjs(values.varSheetIssuedDate).format(DEFAULT_DATE_FORMAT)
        : null,
    });
    if (account.isError()) {
      return reduceServerErrors(account);
    }
    /* End update account */
    /* Update Account FI */
    await updateAccountFI({
      accountId: user.accountId,
      financialInstitutionId:
        user.financialInstitutionId || account.financialInstitutions[0].id,
      currentCustomer: values.currentCustomer
        ? values.currentCustomer.value
        : null,
      memberId: values.memberId || null,
    });
    if (account.isError()) {
      return reduceServerErrors(account);
    }
    /* End Update Account FI */

    if (!forInsurance) {
      const deletedWebsites = differenceBy(
        account.websites,
        values.websites.map(({ label, value }) => ({ url: label, id: value })),
        'id'
      );
      const addedWebsites = values.websites
        .filter(
          ({ value, label }) => typeof value === 'string' && value === label
        )
        .map(({ value }) => ({
          url: value,
          accountId: user.accountId,
        }));

      /* Delete websites */
      await Promise.all(
        deletedWebsites.map(({ id }) =>
          deleteAccountWebsite({
            websiteId: id,
            accountId: user.accountId,
          })
        )
      );
      if (account.isError()) {
        return reduceServerErrors(account);
      }
      /* End delete websites */

      /* Add websites */
      await Promise.all(addedWebsites.map(addAccountWebsite));
      if (account.isError()) {
        return reduceServerErrors(account);
      }
      /* End add websites */

      const deletedDBAs = differenceBy(
        account.dbas,
        values.dbas.map(({ label, value }) => ({
          name: label,
          id: value,
        })),
        'id'
      );
      const addedDBAs = values.dbas
        .filter(
          ({ value, label }) => typeof value === 'string' && value === label
        )
        .map(({ value }) => ({
          name: value,
          accountId: user.accountId,
        }));

      /* Deleted DBAs */
      await Promise.all(
        deletedDBAs.map(({ id }) =>
          deleteAccountDBA({
            dbaId: id,
            accountId: user.accountId,
          })
        )
      );
      /* End delete DBAs */

      /* Add DBAs */
      await Promise.all(addedDBAs.map(addAccountDBA));
      if (account.isError()) {
        return reduceServerErrors(account);
      }
      /* End add DBAs */

      /* Add/update locations */
      const getLocValues = (values, locationType) =>
        values[titleToCamelCase(locationType.name)];

      await Promise.all(
        LOCATION_TYPES.map(async (locationType, i) => {
          const locValues =
            i > 0 && values.addressSameAsAbove
              ? getLocValues(values, LOCATION_TYPES[0])
              : getLocValues(values, locationType);
          const existingLocation = account.locations.find(
            ({ typeId }) => typeId === locationType.id
          );
          if (existingLocation) {
            await updateAccountLocation({
              ...locValues,
              accountId: user.accountId,
              locationId: existingLocation.id,
              addressType: locationType,
            });
          } else {
            await addAccountLocation({
              ...locValues,
              accountId: user.accountId,
              locationId: null,
              addressType: locationType,
            });
          }
        })
      );
    }

    if (account.isError()) {
      return reduceServerErrors(account);
    }
    /* End add/update locations */

    toast(UiToastAdapter('Business data saved successfully!', 'success'));
    if (goNext && !forInsurance) {
      history.push(nextPage);
    }
    return undefined;
  };

  const buildInitialValues = () => {
    let { taxId } = account;
    if (taxId) {
      taxId = account.taxId.toString();
      taxId = `${taxId.substr(0, 2)}-${taxId.substr(2)}`;
    }
    const newInitialValues = {
      ...account,
      businessSince: account.businessSince
        ? dayjs(account.businessSince)
            .utc()
            .toDate()
        : null,
      currentCustomer: find(currentCustomerOptions, {
        value: get(account, 'financialInstitutions[0].currentCustomer', false),
      }),
      memberId: get(account, 'financialInstitutions[0].memberId', null),
      naics: (account.naics || '').toString(),
      taxId,
      industries: account.industries.map(id =>
        find(formData.industries, { id })
      ),
      primaryPhone: account.primaryPhone && maskPhone(account.primaryPhone),
      customerServicePhone:
        account.customerServicePhone && maskPhone(account.customerServicePhone),

      otherPhone: account.otherPhone && maskPhone(account.otherPhone),
      facebook: account.facebook || FACEBOOK_BASE_URL,
      instagram: account.instagram || INSTAGRAM_BASE_URL,
      twitter: account.twitter || TWITTER_BASE_URL,
      linkedin: account.linkedin || LINKEDIN_BASE_URL,
      services: account.servicesOffered.map(service =>
        find(formData.services, { id: service.id })
      ),
      websites: account.websites.map(({ url, id }) => ({
        label: url,
        value: id,
      })),
      dbas: account.dbas.map(({ name, id }) => ({
        label: name,
        value: id,
      })),
      groupNames: account.insuranceGroupNames.map(({ name, id }) => ({
        label: name,
        value: id,
      })),
      entityType: find(ACCOUNT_ENTITY_TYPES, { value: account.entityType }),
      incorporationState: find(usStates, {
        value: account.incorporationState,
      }),
      entityStartDate: account.entityStartDate
        ? dayjs(account.entityStartDate)
            .utc()
            .toDate()
        : null,
      varSheetIssuedDate: account.varSheetIssuedDate
        ? dayjs(account.varSheetIssuedDate)
            .utc()
            .toDate()
        : null,
      ...Object.fromEntries(
        LOCATION_TYPES.map(type => [
          titleToCamelCase(type.name),
          {
            type,
            ...getLocation(type),
          },
        ])
      ),
      addressSameAsAbove: false,
    };
    setInitialValues(newInitialValues);
  };

  React.useEffect(() => {
    setBreadcrumbs([{ label: 'Business', href: '/business' }]);
    (async () => {
      if (
        (account.isEmpty() || formData.isEmpty()) &&
        !(account.isLoading() || formData.isLoading())
      ) {
        setLoading(true);
        await fetchFormData(fiId, user.accountId);
      }
      // buildInitialValues();
      setLoading(false);
    })();
  }, []);

  React.useEffect(() => {
    if (account.isError()) {
      reduceServerErrors(account);
    }
    if (!formData.isLoading()) {
      buildInitialValues();
    }
  }, [account]);

  return (
    <>
      <MuiTitle isSize="4" hasTextColor="info" tag="h1">
        Business Profile
      </MuiTitle>

      <Columns className="has-guidelines">
        <Column>
          <Form
            onSubmit={handleSubmit}
            subscription={{
              submitting: true,
              dirty: true,
              hasValidationErrors: true,
              dirtySinceLastSubmit: true,
              values: { currentCustomer: true },
              // errors: true  // Useful for debugging
            }}
            initialValues={initialValues}
            mutators={{
              cleanse: ([event, cleansing], state, { changeValue }) => {
                cleanseInput(event, state, changeValue, cleansing);
              },
              ...arrayMutators,
            }}
            render={({
              handleSubmit,
              submitting,
              values,
              hasValidationErrors,
              dirty,
              dirtySinceLastSubmit,
              form,
              // errors  // Useful for debugging
            }) => {
              const isCurrentCustomer =
                !!values.currentCustomer && values.currentCustomer.value;
              return (
                <form onSubmit={handleSubmit} noValidate>
                  <div className={toastContainerClassName}>
                    <SaveChangesToast
                      message="Click the button to the right to save your changes"
                      isLoading={submitting}
                      isDisabled={submitting || hasValidationErrors}
                      isVisible={dirty || dirtySinceLastSubmit}
                      onClickButton={showModal}
                    />
                  </div>
                  <Card>
                    {!forInsurance && (
                      <CardContent>
                        <UiFieldset
                          title="General Information"
                          tooltip="All company information provided must be valid"
                          isFirst
                        >
                          <Columns isMultiline>
                            <Column
                              className="is-one-third-fullhd"
                              isSize="1/2"
                            >
                              <Field
                                component={UiSelectAdapter}
                                name="currentCustomer"
                                id="currentCustomer"
                                autoFocus
                                required
                                options={currentCustomerOptions}
                                label="New or Current Customer"
                                validate={composeValidators(
                                  'New or Current Customer',
                                  required
                                )}
                              />
                            </Column>
                            <Column
                              className="is-one-third-fullhd"
                              isSize="1/2"
                            >
                              <Field
                                component={UiInputAdapter}
                                name="memberId"
                                id="memberId"
                                label="Member # / CIF #"
                                disabled={submitting || !isCurrentCustomer}
                              />
                            </Column>
                            <Column
                              className="is-one-third-fullhd"
                              isSize="1/2"
                            >
                              <Field
                                component={UiInputAdapter}
                                name="businessName"
                                id="businessName"
                                label="Legal Business Name"
                                disabled={submitting}
                                required
                                validate={composeValidators(
                                  'Business Name',
                                  required
                                )}
                              />
                            </Column>
                            <Column
                              className="is-one-third-fullhd"
                              isSize="1/2"
                            >
                              <Field
                                component={UiDatePickerAdapter}
                                name="businessSince"
                                id="businessSince"
                                label="Business Since"
                                disabled={submitting}
                                required
                                maxDate={dayjs()
                                  .startOf('day')
                                  .toDate()}
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                hasIcons="right"
                                renderIcons={() => [
                                  <span
                                    className="icon is-right"
                                    key="right-icon"
                                  >
                                    <FontAwesomeIcon
                                      icon={['fal', 'calendar-alt']}
                                    />
                                  </span>,
                                ]}
                                validate={composeValidators(
                                  'Business Since',
                                  required
                                )}
                              />
                            </Column>
                            <Column
                              className="is-one-third-fullhd"
                              isSize="1/2"
                            >
                              <Field
                                component={UiSelectAdapter}
                                name="industries"
                                id="industries"
                                required
                                options={formData.industries || []}
                                label="Industry"
                                isMulti
                                getOptionLabel={item => item.name}
                                getOptionValue={item => item.id}
                                validate={composeValidators(
                                  'Industries',
                                  required
                                )}
                              />
                            </Column>
                            <Column
                              className="is-one-third-fullhd"
                              isSize="1/2"
                            >
                              <Field
                                component={UiInputAdapter}
                                name="taxId"
                                id="taxId"
                                label="Tax ID"
                                disabled={submitting}
                                required
                                mask={TAX_ID_MASK}
                                guide={false}
                                validate={composeValidators('Tax ID', required)}
                              />
                            </Column>
                            <Column
                              className="is-one-third-fullhd"
                              isSize="1/2"
                            >
                              <Field
                                component={UiInputAdapter}
                                name="naics"
                                id="naics"
                                label="NAICS Code"
                                disabled={submitting}
                                maxLength="8"
                              />
                            </Column>
                            <Column
                              className="is-one-third-fullhd"
                              isSize="1/2"
                            >
                              <Field
                                component={UiInputAdapter}
                                name="businessEmail"
                                id="businessEmail"
                                validate={composeValidators(
                                  'Email Address',
                                  required,
                                  email
                                )}
                                label="Email Address"
                                disabled={submitting}
                                required
                                renderIcons={() => [
                                  <span
                                    className="icon is-small is-right"
                                    key="right-icon"
                                  >
                                    <FontAwesomeIcon
                                      icon={['fal', 'envelope']}
                                    />
                                  </span>,
                                ]}
                                hasIcons="right"
                              />
                            </Column>
                            <Column
                              className="is-one-third-fullhd"
                              isSize="1/2"
                            >
                              <Field
                                component={UiSelectAdapter}
                                name="websites"
                                id="websites"
                                validate={composeValidators(
                                  'Websites',
                                  required,
                                  url
                                )}
                                label="Websites"
                                disabled={submitting}
                                required
                                type="url"
                                isCreatable
                                components={{
                                  DropdownIndicator: null,
                                }}
                                isMulti
                                placeholder="Type/paste and press enter..."
                                menuIsOpen={false}
                              />
                            </Column>
                            <Column
                              className="is-one-third-fullhd"
                              isSize="1/2"
                            >
                              <Field
                                component={UiInputAdapter}
                                name="primaryPhone"
                                id="primaryPhone"
                                label="Primary Phone"
                                disabled={submitting}
                                required
                                renderIcons={() => [
                                  <span
                                    className="icon is-small is-right"
                                    key="right-icon"
                                  >
                                    <FontAwesomeIcon icon={['fal', 'phone']} />
                                  </span>,
                                ]}
                                hasIcons="right"
                                mask={PHONE_MASK}
                                guide={false}
                                type="tel"
                                validate={composeValidators(
                                  'Primary Phone',
                                  required
                                )}
                              />
                            </Column>
                            <Column
                              className="is-one-third-fullhd"
                              isSize="1/2"
                            >
                              <Field
                                component={UiInputAdapter}
                                name="otherPhone"
                                id="otherPhone"
                                label="Other Phone"
                                disabled={submitting}
                                renderIcons={() => [
                                  <span
                                    className="icon is-small is-right"
                                    key="right-icon"
                                  >
                                    <FontAwesomeIcon icon={['fal', 'phone']} />
                                  </span>,
                                ]}
                                hasIcons="right"
                                mask={PHONE_MASK}
                                guide={false}
                                type="tel"
                              />
                            </Column>
                            <Column
                              className="is-one-third-fullhd"
                              isSize="1/2"
                            >
                              <Field
                                component={UiSelectAdapter}
                                name="dbas"
                                id="dbas"
                                validate={composeValidators('DBA', required)}
                                type="text"
                                label="DBA"
                                disabled={submitting}
                                required
                                isCreatable
                                components={{
                                  DropdownIndicator: null,
                                }}
                                isMulti
                                placeholder="Type/paste and press enter..."
                                menuIsOpen={false}
                              />
                            </Column>
                            <Column
                              className="is-one-third-fullhd"
                              isSize="1/2"
                            >
                              <Field
                                component={UiSelectAdapter}
                                name="entityType"
                                id="entityType"
                                validate={composeValidators(
                                  'Entity Type',
                                  required
                                )}
                                label="Entity Type"
                                required
                                options={ACCOUNT_ENTITY_TYPES}
                              />
                            </Column>
                            <Column
                              className="is-one-third-fullhd"
                              isSize="1/2"
                            >
                              <Field
                                component={UiSelectAdapter}
                                name="incorporationState"
                                id="incorporationState"
                                validate={composeValidators(
                                  'Incorporation State',
                                  required
                                )}
                                label="Incorporation State"
                                required
                                options={usStates}
                              />
                            </Column>
                            <Column
                              className="is-one-third-fullhd"
                              isSize="1/2"
                            >
                              <Field
                                component={UiInputAdapter}
                                name="currentOwnership"
                                id="currentOwnership"
                                validate={composeValidators(
                                  'Years of Current Ownership',
                                  required,
                                  number({ min: 0, max: 100 })
                                )}
                                label="Years of Current Ownership"
                                onChange={e =>
                                  form.mutators.cleanse(e, cleanseInt)
                                }
                                required
                              />
                            </Column>
                            <Column
                              className="is-one-third-fullhd"
                              isSize="1/2"
                            >
                              <Field
                                component={UiDatePickerAdapter}
                                name="entityStartDate"
                                id="entityStartDate"
                                validate={composeValidators(
                                  'Entity Start Date',
                                  required
                                )}
                                label="Entity Start Date"
                                maxDate={dayjs()
                                  .startOf('day')
                                  .toDate()}
                                required
                                type="date"
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                hasIcons="right"
                                renderIcons={() => [
                                  <span
                                    className="icon is-small is-right"
                                    key="right-icon"
                                  >
                                    <FontAwesomeIcon
                                      icon={['fal', 'calendar-alt']}
                                      size="sm"
                                    />
                                  </span>,
                                ]}
                              />
                            </Column>
                            <Column
                              className="is-one-third-fullhd"
                              isSize="1/2"
                            >
                              <Field
                                component={UiInputAdapter}
                                name="customerServiceEmail"
                                id="customerServiceEmail"
                                validate={composeValidators(
                                  'Customer Service Email Address',
                                  required,
                                  email
                                )}
                                label="Customer Service Email Address"
                                disabled={submitting}
                                renderIcons={() => [
                                  <span
                                    className="icon is-small is-right"
                                    key="right-icon"
                                  >
                                    <FontAwesomeIcon
                                      icon={['fal', 'envelope']}
                                    />
                                  </span>,
                                ]}
                                hasIcons="right"
                                required
                              />
                            </Column>
                            <Column
                              className="is-one-third-fullhd"
                              isSize="1/2"
                            >
                              <Field
                                component={UiInputAdapter}
                                name="customerServicePhone"
                                id="customerServicePhone"
                                label="Customer Service Phone"
                                disabled={submitting}
                                renderIcons={() => [
                                  <span
                                    className="icon is-small is-right"
                                    key="right-icon"
                                  >
                                    <FontAwesomeIcon icon={['fal', 'phone']} />
                                  </span>,
                                ]}
                                hasIcons="right"
                                mask={PHONE_MASK}
                                guide={false}
                                type="tel"
                                validate={composeValidators(
                                  'Customer Service Phone',
                                  required
                                )}
                                required
                              />
                            </Column>
                          </Columns>
                          <Columns isMultiline>
                            <Column isSize="full">
                              <Field
                                component={UiQuillAdapter}
                                name="briefDescription"
                                id="briefDescription"
                                label="Brief Business Description"
                                validate={composeValidators(
                                  'Brief Business Description',
                                  required
                                )}
                                placeholder="Please provide a brief description of your business"
                                required
                              />
                            </Column>
                          </Columns>
                        </UiFieldset>
                        {LOCATION_TYPES.map((type, i) => (
                          <AddressForm
                            name={type.name}
                            key={type.id}
                            useSameAs={i > 0}
                            values={values}
                          />
                        ))}
                        {(user.isBankAdmin || user.isSuperAdmin) &&
                          // TODO: this should be at fiaccount level, not fi level
                          user.financialInstitutionForPayments && (
                            <UiFieldset title="Payments Information">
                              <Columns isMultiline>
                                <Column
                                  isSize="1/2"
                                  className="is-one-third-fullhd"
                                >
                                  <Field
                                    component={UiDatePickerAdapter}
                                    name="varSheetIssuedDate"
                                    id="varSheetIssuedDate"
                                    validate={composeValidators(
                                      'VAR Sheet Issued Date'
                                    )}
                                    label="VAR Sheet Issued Date"
                                    maxDate={dayjs()
                                      .startOf('day')
                                      .toDate()}
                                    type="date"
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    hasIcons="right"
                                    renderIcons={() => [
                                      <span
                                        className="icon is-small is-right"
                                        key="right-icon"
                                      >
                                        <FontAwesomeIcon
                                          icon={['fal', 'calendar-alt']}
                                          size="sm"
                                        />
                                      </span>,
                                    ]}
                                  />

                                  <Field
                                    component={UiInputAdapter}
                                    name="reportingMid"
                                    id="reportingMid"
                                    label="Reporting (MID)"
                                  />
                                </Column>
                                <Column
                                  isSize="1/2"
                                  className="is-one-third-fullhd"
                                >
                                  <Field
                                    component={UiInputAdapter}
                                    name="varSheetMid"
                                    id="varSheetMid"
                                    label="VAR Sheet MID"
                                  />

                                  <Field
                                    component={UiToggleAdapter}
                                    id="midActiveToggle"
                                    name="midActiveToggle"
                                    type="checkbox"
                                  >
                                    Enable MID for Reports
                                  </Field>
                                  <Field
                                    component={UiToggleAdapter}
                                    id="acceptsTips"
                                    name="acceptsTips"
                                    type="checkbox"
                                  >
                                    Account Accepts Tips
                                  </Field>
                                </Column>
                              </Columns>
                            </UiFieldset>
                          )}
                        <UiFieldset title="Social Media">
                          <Columns isMultiline>
                            <Column isSize="1/2">
                              <Field
                                component={UiInputAdapter}
                                name="facebook"
                                id="facebook"
                                validate={composeValidators('Facebook', url)}
                                label="Facebook"
                                disabled={submitting}
                                renderIcons={() => [
                                  <span
                                    className="icon is-small is-right"
                                    key="right-icon"
                                  >
                                    <FontAwesomeIcon icon={['fal', 'link']} />
                                  </span>,
                                ]}
                                hasIcons="right"
                                type="url"
                              />
                            </Column>
                            <Column isSize="1/2">
                              <Field
                                component={UiInputAdapter}
                                name="linkedin"
                                id="linkedin"
                                validate={composeValidators('Linkedin', url)}
                                label="Linkedin"
                                disabled={submitting}
                                renderIcons={() => [
                                  <span
                                    className="icon is-small is-right"
                                    key="right-icon"
                                  >
                                    <FontAwesomeIcon icon={['fal', 'link']} />
                                  </span>,
                                ]}
                                hasIcons="right"
                                type="url"
                              />
                            </Column>
                            <Column isSize="1/2">
                              <Field
                                component={UiInputAdapter}
                                name="twitter"
                                id="twitter"
                                validate={composeValidators('Twitter', url)}
                                label="Twitter"
                                disabled={submitting}
                                renderIcons={() => [
                                  <span
                                    className="icon is-small is-right"
                                    key="right-icon"
                                  >
                                    <FontAwesomeIcon icon={['fal', 'link']} />
                                  </span>,
                                ]}
                                hasIcons="right"
                                type="url"
                              />
                            </Column>
                            <Column isSize="1/2">
                              <Field
                                component={UiInputAdapter}
                                name="instagram"
                                id="instagram"
                                validate={composeValidators('Instagram', url)}
                                label="Instagram"
                                disabled={submitting}
                                renderIcons={() => [
                                  <span
                                    className="icon is-small is-right"
                                    key="right-icon"
                                  >
                                    <FontAwesomeIcon icon={['fal', 'link']} />
                                  </span>,
                                ]}
                                hasIcons="right"
                                type="url"
                              />
                            </Column>
                          </Columns>
                        </UiFieldset>
                      </CardContent>
                    )}
                    {forInsurance && (
                      <CardContent>
                        <UiFieldset
                          title="General Information"
                          tooltip="All company information provided must be valid"
                          isFirst
                        >
                          <Columns isMultiline>
                            <Column
                              className="is-one-third-fullhd"
                              isSize="1/2"
                            >
                              <Field
                                component={UiInputAdapter}
                                name="businessName"
                                id="businessName"
                                label="Legal Business Name"
                                disabled={submitting}
                                required
                                validate={composeValidators(
                                  'Business Name',
                                  required
                                )}
                              />
                            </Column>
                            <Column
                              className="is-one-third-fullhd"
                              isSize="1/2"
                            >
                              <Field
                                component={UiInputAdapter}
                                name="businessEmail"
                                id="businessEmail"
                                label="Email Address"
                                disabled={submitting}
                                // required
                                renderIcons={() => [
                                  <span
                                    className="icon is-small is-right"
                                    key="right-icon"
                                  >
                                    <FontAwesomeIcon
                                      icon={['fal', 'envelope']}
                                    />
                                  </span>,
                                ]}
                                hasIcons="right"
                              />
                            </Column>
                            <Column
                              className="is-one-third-fullhd"
                              isSize="1/2"
                            >
                              <Field
                                component={UiInputAdapter}
                                name="primaryPhone"
                                id="primaryPhone"
                                label="Primary Phone"
                                disabled={submitting}
                                // required
                                renderIcons={() => [
                                  <span
                                    className="icon is-small is-right"
                                    key="right-icon"
                                  >
                                    <FontAwesomeIcon icon={['fal', 'phone']} />
                                  </span>,
                                ]}
                                hasIcons="right"
                                mask={PHONE_MASK}
                                guide={false}
                                type="tel"
                                // validate={composeValidators(
                                //   'Primary Phone',
                                //   required
                                // )}
                              />
                            </Column>
                            <Column
                              className="is-one-third-fullhd"
                              isSize="1/2"
                            >
                              <Field
                                component={UiSelectAdapter}
                                name="groupNames"
                                id="groupNames"
                                // validate={composeValidators('DBA', required)}
                                type="text"
                                label="Group Names"
                                disabled={submitting}
                                // required
                                isCreatable
                                components={{
                                  DropdownIndicator: null,
                                }}
                                isMulti
                                placeholder="Type/paste and press enter..."
                                menuIsOpen={false}
                              />
                            </Column>
                          </Columns>
                        </UiFieldset>
                      </CardContent>
                    )}
                    <InnerLoader visible={loading || submitting} />
                  </Card>
                  <ConfirmChangesModal
                    title="Business Profile"
                    isActive={confirmChangesModal}
                    onClickCancel={hideModal}
                    onClickSubmit={hideModal}
                  />
                </form>
              );
            }}
          />
        </Column>
        {!user.isBankAdmin && !forInsurance && (
          <Column isSize="narrow" className="guidelines-container">
            <GuidelinesCard guidelines={guidelines} />
          </Column>
        )}
      </Columns>
    </>
  );
};

BusinessPage.propTypes = {
  isExpanded: propTypes.bool.isRequired,
  setBreadcrumbs: propTypes.func.isRequired,
  fetchFormData: propTypes.func.isRequired,
  updateAccount: propTypes.func.isRequired,
  updateAccountFI: propTypes.func.isRequired,
  addAccountWebsite: propTypes.func.isRequired,
  deleteAccountWebsite: propTypes.func.isRequired,
  addAccountLocation: propTypes.func.isRequired,
  updateAccountLocation: propTypes.func.isRequired,
  addAccountDBA: propTypes.func.isRequired,
  deleteAccountDBA: propTypes.func.isRequired,
  account: propTypes.object.isRequired,
  user: propTypes.object.isRequired,
  formData: propTypes.object.isRequired,
  history: propTypes.object.isRequired,
};

function mapStateToProps(state) {
  return {
    user: getUser(state),
    account: getAccount(state),
    formData: getFormData(state),
  };
}

const connectedBusinessPage = React.memo(
  connect(mapStateToProps, {
    setBreadcrumbs,
    fetchFormData,
    updateAccount,
    updateAccountFI,
    addAccountWebsite,
    deleteAccountWebsite,
    addAccountDBA,
    deleteAccountDBA,
    addAccountLocation,
    updateAccountLocation,
  })(withNavigation(BusinessPage))
);

export { connectedBusinessPage as BusinessPage };
